/**
 * BlockContainer - Orchestrates all enabled blocks
 *
 * Fetches the list of enabled blocks for the client with should_show filtering,
 * then renders a BlockLoader for each block that should be shown.
 *
 * The API now returns should_show for each block, which considers:
 * - User's dismiss/remind_later state
 * - Visibility settings (members only, anonymous only)
 * - Whether the block has content to display
 *
 * Supports auth session simulation in dev mode via DevModeContext.
 */
import { useState, useEffect, useCallback, Suspense } from 'react';
import { fetchEnabledBlocks } from '../lib/api';
import { useDevMode } from '../context/DevModeContext';
import { useClient } from '../context/ClientContext';
import BlockLoader from './BlockLoader';

export default function BlockContainer({ clientId }) {
  const { authSessionId, profile } = useDevMode();
  const { clientConfig } = useClient();
  const memberId = profile?.member_id || null;
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failedBlocks, setFailedBlocks] = useState(new Set());

  const loadBlocks = useCallback(async () => {
    if (!clientId) return;

    console.log(
      '🔲 [BlockContainer] Loading blocks for client:',
      clientId,
      'authSessionId:',
      authSessionId,
    );

    try {
      setLoading(true);
      // Explicitly request landing page blocks only
      const options = {
        location: 'landing',
        ...(authSessionId && { authSessionId }),
      };
      console.log('🔲 [BlockContainer] Fetching with options:', options);
      const result = await fetchEnabledBlocks(clientId, options);
      console.log('🔲 [BlockContainer] API response:', result);

      // Filter to only blocks that should be shown, then sort by order
      const visibleBlocks = (result.blocks || [])
        .filter((block) => block.should_show !== false)
        .sort((a, b) => a.order - b.order);

      console.log(
        '🔲 [BlockContainer] Visible blocks:',
        visibleBlocks.map((b) => b.id),
      );
      setBlocks(visibleBlocks);
    } catch (err) {
      console.error('🔲 [BlockContainer] Failed to load blocks list:', err);
      // Silently fail - blocks are optional content
      setBlocks([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, authSessionId]);

  useEffect(() => {
    loadBlocks();
  }, [loadBlocks]);

  // Track which blocks failed to load (for potential retry logic)
  const handleBlockError = useCallback((blockId, error) => {
    setFailedBlocks((prev) => new Set([...prev, blockId]));
  }, []);

  // Don't render anything while loading or if no blocks
  if (loading) return null;
  if (blocks.length === 0) return null;

  // Get primary color from client config (for block theming)
  const primaryColor = clientConfig?.primaryColor;

  return (
    <div className="space-y-4 mb-6">
      <Suspense fallback={null}>
        {blocks.map((block) => (
          <BlockLoader
            key={block.id}
            blockId={block.id}
            clientId={clientId}
            authSessionId={authSessionId}
            memberId={memberId}
            primaryColor={primaryColor}
            onError={handleBlockError}
          />
        ))}
      </Suspense>
    </div>
  );
}
